import { useId } from 'react'

import type {
    RefundFieldErrors,
    RefundFormValue,
} from '@/lib/schemas/refund-schemas'
import type { RefundMode } from '@/lib/api/sales-return-api'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

interface Props {
    value: RefundFormValue
    onChange: (value: RefundFormValue) => void
    /** Số tiền khách đã trả thừa — hoàn tối đa. ≤ 0 thì không render. */
    maxRefundable: number
    /** Người dùng có quyền customers.refund. */
    canRefund: boolean
    errors?: RefundFieldErrors
}

const MODES: { mode: RefundMode; label: (max: number) => string }[] = [
    { mode: 'none', label: () => m.refund_mode_none() },
    {
        mode: 'full',
        label: (max) => m.refund_mode_full({ amount: formatVnd(max) }),
    },
    { mode: 'partial', label: () => m.refund_mode_partial() },
]

/**
 * Khối "Hoàn tiền cho khách" (M-44) dùng chung cho trả hàng, xác nhận hoàn và
 * huỷ đơn. Không hoàn thì phần khách trả thừa thành tiền dư (số dư âm) để trừ
 * vào đơn sau.
 */
export function RefundSection({
    value,
    onChange,
    maxRefundable,
    canRefund,
    errors = {},
}: Props) {
    const baseId = useId()
    if (maxRefundable <= 0) return null
    const set = (patch: Partial<RefundFormValue>) =>
        onChange({ ...value, ...patch })
    const amountId = `${baseId}-amount`
    const amountErrorId = `${amountId}-error`
    const methodErrorId = `${baseId}-method-error`

    return (
        <section
            className="space-y-3 rounded-md border p-3"
            aria-labelledby={`${baseId}-title`}
        >
            <div>
                <h3 id={`${baseId}-title`} className="text-sm font-semibold">
                    {m.refund_title()}
                </h3>
                <p className="text-xs text-muted-foreground">
                    {m.refund_max_hint({ amount: formatVnd(maxRefundable) })}
                </p>
            </div>
            {!canRefund ? (
                <p className="text-xs text-muted-foreground">
                    {m.refund_no_permission()}
                </p>
            ) : (
                <>
                    <div
                        role="radiogroup"
                        aria-label={m.refund_title()}
                        className="flex flex-wrap gap-2"
                    >
                        {MODES.map((opt) => (
                            <label
                                key={opt.mode}
                                className={cn(
                                    'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                                    value.mode === opt.mode &&
                                        'border-primary bg-primary/5',
                                )}
                            >
                                <input
                                    type="radio"
                                    name={`${baseId}-mode`}
                                    className="h-4 w-4"
                                    checked={value.mode === opt.mode}
                                    onChange={() => set({ mode: opt.mode })}
                                />
                                {opt.label(maxRefundable)}
                            </label>
                        ))}
                    </div>
                    {value.mode === 'none' && (
                        <p className="text-xs text-muted-foreground">
                            {m.refund_none_hint()}
                        </p>
                    )}
                    {value.mode === 'partial' && (
                        <div className="space-y-1">
                            <Label htmlFor={amountId} required>
                                {m.refund_amount_label()}
                            </Label>
                            <Input
                                id={amountId}
                                type="number"
                                inputMode="numeric"
                                value={value.amount}
                                aria-invalid={Boolean(errors.amount)}
                                aria-describedby={
                                    errors.amount
                                        ? amountErrorId
                                        : `${amountId}-preview`
                                }
                                onChange={(e) =>
                                    set({ amount: e.target.value })
                                }
                                className="sm:w-60"
                            />
                            <MoneyPreview
                                id={`${amountId}-preview`}
                                value={value.amount}
                            />
                            {errors.amount && (
                                <p
                                    id={amountErrorId}
                                    className="text-xs text-destructive"
                                >
                                    {errors.amount}
                                </p>
                            )}
                        </div>
                    )}
                    {value.mode !== 'none' && (
                        <div className="space-y-1">
                            <Label required>{m.refund_method_label()}</Label>
                            <div
                                role="radiogroup"
                                aria-label={m.refund_method_label()}
                                aria-describedby={
                                    errors.payment_method
                                        ? methodErrorId
                                        : undefined
                                }
                                className="flex flex-wrap gap-2"
                            >
                                {(['cash', 'bank_transfer'] as const).map(
                                    (method) => (
                                        <label
                                            key={method}
                                            className={cn(
                                                'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                                                value.payment_method ===
                                                    method &&
                                                    'border-primary bg-primary/5',
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name={`${baseId}-method`}
                                                className="h-4 w-4"
                                                checked={
                                                    value.payment_method ===
                                                    method
                                                }
                                                onChange={() =>
                                                    set({
                                                        payment_method: method,
                                                    })
                                                }
                                            />
                                            {method === 'cash'
                                                ? m.payment_method_cash()
                                                : m.payment_method_bank()}
                                        </label>
                                    ),
                                )}
                            </div>
                            {errors.payment_method && (
                                <p
                                    id={methodErrorId}
                                    className="text-xs text-destructive"
                                >
                                    {errors.payment_method}
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
        </section>
    )
}
